import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Search, ChevronRight, ChevronLeft, ShoppingCart } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  postTwilioAvailablePhoneNumbers,
  postTwilioRotateOutboundCaller,
  type OutboundCallerLineRow,
  type TwilioAvailableNumberRow,
  type TwilioPagination,
} from "@/lib/twilio-phone-dashboard-api";
import { cn } from "@/utils";

export type PhoneFlowMode = "replace" | "add";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: PhoneFlowMode;
  /** For ``replace`` — line being rotated; ignored for ``add``. */
  existingLine: OutboundCallerLineRow | null;
  onCompleted: () => void;
};

function buildSearchQuery(
  inRegion: string,
  inPostalCode: string,
  areaCode: string,
  contains: string,
  advancedJson: string,
): Record<string, unknown> {
  const q: Record<string, unknown> = {};
  if (inRegion.trim()) q.in_region = inRegion.trim().toUpperCase();
  if (inPostalCode.trim()) q.in_postal_code = inPostalCode.trim();
  if (areaCode.trim()) {
    const n = parseInt(areaCode.replace(/\D/g, ""), 10);
    if (!Number.isNaN(n)) q.area_code = n;
  }
  if (contains.trim()) q.contains = contains.trim();
  const extra = advancedJson.trim();
  if (extra) {
    const parsed = JSON.parse(extra) as Record<string, unknown>;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      Object.assign(q, parsed);
    }
  }
  return q;
}

function capSummary(row: TwilioAvailableNumberRow): string {
  const c = row.capabilities;
  if (!c) return "—";
  const bits: string[] = [];
  if (c.voice) bits.push("voice");
  if (c.sms) bits.push("sms");
  if (c.mms) bits.push("mms");
  return bits.length ? bits.join(", ") : "—";
}

const STEPS = [
  { n: 1, label: "Search" },
  { n: 2, label: "Select" },
  { n: 3, label: "Buy" },
];

export function PhoneNumberFlowModal({
  open,
  onOpenChange,
  mode,
  existingLine,
  onCompleted,
}: Props) {
  const [step, setStep] = useState(1);

  const [countryCode, setCountryCode] = useState("US");
  const [numberType, setNumberType] = useState<"local" | "mobile">("local");
  const [inRegion, setInRegion] = useState("");
  const [inPostalCode, setInPostalCode] = useState("");
  const [areaCode, setAreaCode] = useState("");
  const [contains, setContains] = useState("");
  const [advancedJson, setAdvancedJson] = useState("");

  const [rows, setRows] = useState<TwilioAvailableNumberRow[]>([]);
  const [pagination, setPagination] = useState<TwilioPagination | null>(null);
  const [searchBusy, setSearchBusy] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const [selected, setSelected] = useState<TwilioAvailableNumberRow | null>(null);
  const [friendlyName, setFriendlyName] = useState("");
  const [buyBusy, setBuyBusy] = useState(false);
  const [buyError, setBuyError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setStep(1);
    setCountryCode("US");
    setNumberType("local");
    setInRegion("");
    setInPostalCode("");
    setAreaCode("");
    setContains("");
    setAdvancedJson("");
    setRows([]);
    setPagination(null);
    setSearchBusy(false);
    setSearchError(null);
    setSelected(null);
    setFriendlyName("");
    setBuyBusy(false);
    setBuyError(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    reset();
    if (mode === "replace" && existingLine) {
      const iso = (existingLine.iso_country as string) || "US";
      const st = (existingLine.provider_state as string) || "";
      const zip = existingLine.provider_postal_code
        ? String(existingLine.provider_postal_code)
        : "";
      setCountryCode(iso);
      setInRegion(st.toUpperCase());
      if (zip) setInPostalCode(zip);
    }
  }, [open, mode, existingLine, reset]);

  /** Firestore rotate target: existing row for replace, else Twilio search country / in region / in postal code. */
  const rotateTarget = useMemo(() => {
    if (mode === "replace" && existingLine) {
      const iso = String(existingLine.iso_country || "US")
        .trim()
        .toUpperCase() || "US";
      const state = String(existingLine.provider_state || "")
        .trim()
        .toUpperCase();
      const raw = existingLine.provider_postal_code;
      const zip =
        raw != null && String(raw).trim() !== "" ? String(raw).trim() : null;
      return { iso, state, zip };
    }
    const iso = (countryCode || "US").trim().toUpperCase() || "US";
    const state = inRegion.trim().toUpperCase();
    const z = inPostalCode.trim();
    return { iso, state, zip: z || null };
  }, [mode, existingLine, countryCode, inRegion, inPostalCode]);

  const fetchTwilioNumbers = useCallback(
    async (pageIndex: number): Promise<{ ok: boolean; resultCount: number }> => {
      setSearchError(null);
      setSearchBusy(true);
      setSelected(null);
      if (pageIndex === 0) {
        setRows([]);
      }
      try {
        let query: Record<string, unknown>;
        try {
          query = buildSearchQuery(
            inRegion,
            inPostalCode,
            areaCode,
            contains,
            advancedJson,
          );
        } catch {
          setSearchError("Advanced query must be valid JSON object.");
          return { ok: false, resultCount: 0 };
        }
        const data = await postTwilioAvailablePhoneNumbers({
          country_code: countryCode.trim() || "US",
          number_type: numberType,
          query,
          page: pageIndex,
        });
        const list = data.available_phone_numbers ?? [];
        setRows(list);
        setPagination(data.pagination ?? null);
        if (pageIndex > 0 && list.length === 0) {
          setSearchError("No more numbers on this page.");
        }
        return { ok: true, resultCount: list.length };
      } catch (e) {
        setSearchError(e instanceof Error ? e.message : "Search failed");
        return { ok: false, resultCount: 0 };
      } finally {
        setSearchBusy(false);
      }
    },
    [
      countryCode,
      numberType,
      inRegion,
      inPostalCode,
      areaCode,
      contains,
      advancedJson,
    ],
  );

  const handleSearchFromStep1 = useCallback(async () => {
    const r = await fetchTwilioNumbers(0);
    if (!r.ok) return;
    if (r.resultCount === 0) {
      setSearchError(
        "No numbers returned for these filters. Adjust options and try again.",
      );
      return;
    }
    setStep(2);
  }, [fetchTwilioNumbers]);

  const canGoToStep3 = Boolean(selected?.phone_number);

  const handleBuy = async () => {
    setBuyError(null);
    const pn = (selected?.phone_number ?? "").trim();
    const st = rotateTarget.state;
    if (!pn) {
      setBuyError("No number selected.");
      return;
    }
    if (st.length !== 2 || !/^[A-Z]{2}$/.test(st)) {
      setBuyError(
        mode === "add"
          ? "Set In region to a two-letter state (e.g. NY) in the search filters before purchasing."
          : "This line is missing a valid provider state.",
      );
      return;
    }
    setBuyBusy(true);
    try {
      await postTwilioRotateOutboundCaller({
        iso_country: rotateTarget.iso || "US",
        provider_state: st,
        provider_postal_code: rotateTarget.zip,
        phone_number: pn,
        twilio_available_snapshot: selected ? { ...selected } : null,
        friendly_name: friendlyName.trim() || null,
      });
      onCompleted();
      onOpenChange(false);
    } catch (e) {
      setBuyError(e instanceof Error ? e.message : "Purchase failed");
    } finally {
      setBuyBusy(false);
    }
  };

  const title =
    mode === "replace"
      ? "Replace outbound line"
      : "Add outbound line";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "flex h-[min(90vh,720px)] max-h-[90vh] w-full max-w-3xl flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl",
        )}
        onPointerDownOutside={(e) => {
          if (buyBusy) e.preventDefault();
        }}
      >
        <div className="shrink-0 border-b border-border p-6 pb-4 pr-14">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription className="sr-only">
              Three-step flow: search Twilio, pick a number, purchase and register.
            </DialogDescription>
          </DialogHeader>
          <ol className="mt-4 flex items-center gap-2 text-sm">
            {STEPS.map((s, i) => (
              <li key={s.n} className="flex flex-1 items-center gap-2">
                <span
                  className={cn(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-semibold",
                    step === s.n
                      ? "border-primary bg-primary text-primary-foreground"
                      : step > s.n
                        ? "border-primary/40 bg-primary/10 text-primary"
                        : "border-muted text-muted-foreground",
                  )}
                >
                  {s.n}
                </span>
                <span
                  className={cn(
                    "hidden font-medium sm:inline",
                    step === s.n ? "text-foreground" : "text-muted-foreground",
                  )}
                >
                  {s.label}
                </span>
                {i < STEPS.length - 1 ? (
                  <ChevronRight className="mx-1 hidden h-4 w-4 shrink-0 text-muted-foreground sm:block" />
                ) : null}
              </li>
            ))}
          </ol>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-6 py-4">
          {step === 1 ? (
            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
              {mode === "replace" && existingLine ? (
                <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">
                  <span className="text-muted-foreground">Replacing</span>{" "}
                  <span className="font-mono font-medium">{existingLine.document_id}</span>
                  <span className="text-muted-foreground"> — current </span>
                  <span className="font-mono font-medium">
                    {String(existingLine.phone_number_e164 ?? "—")}
                  </span>
                </div>
              ) : null}

              <div>
                <h4 className="text-sm font-semibold">Twilio search</h4>
                <p className="mt-1 text-xs text-muted-foreground">
                  {mode === "replace"
                    ? "Filters are pre-filled from this line; adjust and search. The same region is used when you buy."
                    : ""}
                </p>
                <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="space-y-1.5">
                    <span className="text-xs font-medium">Country</span>
                    <Input
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      placeholder="US"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <span className="text-xs font-medium">Number type</span>
                    <Select
                      value={numberType}
                      onValueChange={(v) => setNumberType(v as "local" | "mobile")}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="local">Local</SelectItem>
                        <SelectItem value="mobile">Mobile</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <span className="text-xs font-medium">In region</span>
                    <Input
                      value={inRegion}
                      onChange={(e) => setInRegion(e.target.value)}
                      placeholder="NY"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <span className="text-xs font-medium">In postal code</span>
                    <Input
                      value={inPostalCode}
                      onChange={(e) => setInPostalCode(e.target.value)}
                      placeholder="ZIP"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <span className="text-xs font-medium">Area code</span>
                    <Input
                      value={areaCode}
                      onChange={(e) => setAreaCode(e.target.value)}
                      placeholder="212"
                    />
                  </div>
                  <div className="space-y-1.5 sm:col-span-2">
                    <span className="text-xs font-medium">Contains</span>
                    <Input
                      value={contains}
                      onChange={(e) => setContains(e.target.value)}
                      placeholder="Pattern"
                    />
                  </div>
                </div>
                <div className="mt-3 space-y-1.5">
                  <span className="text-xs font-medium">Advanced (JSON object)</span>
                  <textarea
                    className={cn(
                      "flex min-h-[64px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-xs shadow-sm",
                      "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                    )}
                    value={advancedJson}
                    onChange={(e) => setAdvancedJson(e.target.value)}
                    placeholder='{"exclude_all_address_required": true}'
                    spellCheck={false}
                  />
                </div>
                {searchError ? (
                  <p className="mt-2 text-sm text-destructive" role="alert">
                    {searchError}
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}

          {step === 2 ? (
            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
              <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-muted-foreground">
                  {pagination?.page != null && pagination.page_size != null
                    ? `Page ${pagination.page + 1} · ${pagination.page_size} per request · ${pagination.result_count ?? rows.length} result${(pagination.result_count ?? rows.length) === 1 ? "" : "s"}`
                    : "Twilio results (this page)"}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={searchBusy || (pagination?.page ?? 0) <= 0}
                    onClick={() => {
                      const p = pagination?.page ?? 0;
                      if (p > 0) void fetchTwilioNumbers(p - 1);
                    }}
                  >
                    <ChevronLeft className="mr-1 h-4 w-4" aria-hidden />
                    Previous page
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={searchBusy || pagination?.has_next_page !== true}
                    onClick={() => {
                      const p = pagination?.page ?? 0;
                      void fetchTwilioNumbers(p + 1);
                    }}
                  >
                    Next page
                    <ChevronRight className="ml-1 h-4 w-4" aria-hidden />
                  </Button>
                </div>
              </div>
              {searchError || searchBusy ? (
                <div className="shrink-0 space-y-1">
                  {searchError ? (
                    <p className="text-sm text-destructive" role="alert">
                      {searchError}
                    </p>
                  ) : null}
                  {searchBusy ? (
                    <p className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                      Loading…
                    </p>
                  ) : null}
                </div>
              ) : null}
              <div className="min-h-0 flex-1 overflow-auto rounded-md border border-border">
              <table className="w-full min-w-[560px] text-left text-sm">
                <thead className="sticky top-0 z-10 border-b border-border bg-muted/95 backdrop-blur-sm">
                  <tr>
                    <th className="px-3 py-2 font-medium">Number</th>
                    <th className="px-3 py-2 font-medium">Locality</th>
                    <th className="px-3 py-2 font-medium">Region</th>
                    <th className="px-3 py-2 font-medium">ZIP</th>
                    <th className="px-3 py-2 font-medium">Caps</th>
                    <th className="px-3 py-2"> </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => {
                    const num = row.phone_number ?? "";
                    const isSel = selected?.phone_number === num;
                    return (
                      <tr
                        key={num ? `${num}-${idx}` : `r-${idx}`}
                        className={cn("border-b border-border/80", isSel && "bg-primary/5")}
                      >
                        <td className="px-3 py-2 font-mono text-xs">{num || "—"}</td>
                        <td className="px-3 py-2">{row.locality ?? "—"}</td>
                        <td className="px-3 py-2">{row.region ?? "—"}</td>
                        <td className="px-3 py-2">{row.postal_code ?? "—"}</td>
                        <td className="px-3 py-2 text-muted-foreground">{capSummary(row)}</td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            type="button"
                            variant={isSel ? "secondary" : "outline"}
                            size="sm"
                            onClick={() => setSelected(row)}
                          >
                            {isSel ? "Selected" : "Select"}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          ) : null}

          {step === 3 ? (
            <div className="flex min-h-0 flex-1 flex-col space-y-4 overflow-y-auto">
              <div className="rounded-lg border border-border bg-muted/15 p-4 text-sm">
                <p>
                  <span className="text-muted-foreground">Number to buy:</span>{" "}
                  <span className="font-mono font-medium">
                    {selected?.phone_number ?? "—"}
                  </span>
                </p>
                <p className="mt-2">
                  <span className="text-muted-foreground">Saved as caller ID for:</span>{" "}
                  <span className="font-mono text-xs">
                    {rotateTarget.iso} / {rotateTarget.state || "?"}
                    {rotateTarget.zip ? ` / ${rotateTarget.zip}` : " (state-wide)"}
                  </span>
                </p>
              </div>
              <div className="space-y-1.5">
                <span className="text-sm font-medium">Friendly name (optional)</span>
                <Input
                  value={friendlyName}
                  onChange={(e) => setFriendlyName(e.target.value)}
                  placeholder="e.g. Outbound NY"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Charges Twilio, registers the number with Vapi, and saves this row so matching cases
                use it as outbound caller ID.
              </p>
              {buyError ? (
                <p className="text-sm text-destructive" role="alert">
                  {buyError}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        <DialogFooter className="shrink-0 flex-col gap-3 border-t border-border p-6 sm:flex-row sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {step > 1 ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep((s) => Math.max(1, s - 1))}
                disabled={buyBusy}
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                Back
              </Button>
            ) : null}
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={buyBusy}>
              Cancel
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {step === 1 ? (
              <Button
                type="button"
                onClick={() => void handleSearchFromStep1()}
                disabled={searchBusy || buyBusy}
              >
                {searchBusy ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Search className="mr-2 h-4 w-4" aria-hidden />
                )}
                Search Twilio
              </Button>
            ) : null}
            {step === 2 ? (
              <Button type="button" onClick={() => setStep(3)} disabled={!canGoToStep3}>
                Next: Buy
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            ) : null}
            {step === 3 ? (
              <Button type="button" onClick={() => void handleBuy()} disabled={buyBusy}>
                {buyBusy ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <ShoppingCart className="mr-2 h-4 w-4" aria-hidden />
                )}
                Purchase &amp; activate
              </Button>
            ) : null}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
